import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { mediaUrl } from "@/lib/api";
import { formatTime, formatBytes } from "@/lib/format";
import { FileText, Download, Check, CheckCheck, Phone, Languages, X } from "lucide-react";
import { VoiceNote } from "./VoiceNote";

const URL_RE = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;

// Open a link in the real browser. Inside an embed iframe we ask the host page
// (embed.js) to open it — so an app WebView can hand it to the system browser
// instead of navigating inside the chat.
function openLink(e, href) {
  e.preventDefault();
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: "superchat:open-url", url: href }, "*");
    }
  } catch (_) {}
  try {
    window.open(href, "_blank", "noopener,noreferrer");
  } catch (_) {}
}

// Turn URLs in message text into clickable links.
function linkify(text, linkClass) {
  if (!text || !URL_RE.test(text)) return text;
  URL_RE.lastIndex = 0;
  const nodes = [];
  let last = 0;
  let m;
  while ((m = URL_RE.exec(text)) !== null) {
    let url = m[0];
    let trail = "";
    const t = url.match(/[.,!?)\]}'">]+$/);
    if (t) { trail = t[0]; url = url.slice(0, -trail.length); }
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const href = url.startsWith("http") ? url : `https://${url}`;
    nodes.push(
      <a key={m.index} href={href} target="_blank" rel="noreferrer noopener" onClick={(e) => openLink(e, href)} className={linkClass}>{url}</a>
    );
    if (trail) nodes.push(trail);
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function MessageBubble({ msg, mine, tail = true, surface = "light", onVoiceTranslate, translationActive = false }) {
  const [open, setOpen] = useState(false);
  const [showOrig, setShowOrig] = useState(false);
  const src = mediaUrl(msg.media_url);
  const dark = surface === "dark";
  const time = formatTime(msg.created_at);

  // Show the OTHER party's messages translated into the reader's language;
  // your own messages always show as you typed them.
  const hasTranslation = !mine && msg.translation && msg.translation !== msg.content;
  const textShown = hasTranslation && !showOrig ? msg.translation : msg.content;

  // Close the image lightbox on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const bubbleBase = "relative max-w-[85%] rounded-2xl px-2.5 py-1.5 text-[14.5px] leading-relaxed md:max-w-[78%]";
  let bubbleColor;
  if (dark) {
    bubbleColor = mine
      ? `bg-gradient-to-br from-[#166534] to-[#0f3d24] border border-[#22c55e]/30 text-white shadow-[0_4px_14px_rgba(34,197,94,0.14)] ${tail ? "rounded-tr-md" : ""}`
      : `bg-white/[0.06] backdrop-blur-md border border-white/10 text-gray-100 ${tail ? "rounded-tl-md" : ""}`;
  } else {
    bubbleColor = mine
      ? `bg-[#d9fdd3] text-[#111b18] shadow-sm ${tail ? "rounded-tr-none" : ""}`
      : `bg-white text-[#111b18] shadow-sm ${tail ? "rounded-tl-none" : ""}`;
  }

  const metaColor = dark ? "text-white/45" : "text-black/45";
  const mediaBg = dark ? "bg-white/10 hover:bg-white/15" : "bg-black/5 hover:bg-black/10";
  const fileIconBg = dark ? "bg-[#2ac67b] text-black" : "bg-[#0e8f7e] text-white";

  return (
    <div className={`flex w-full sc-fade-up ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${msg.id}`}>
      <div className={`${bubbleBase} ${bubbleColor}`}>
        {msg.message_type === "image" && (
          <>
            <img
              src={src}
              alt={msg.media_filename || "image"}
              onClick={() => setOpen(true)}
              loading="lazy"
              className="mb-1 block h-auto max-h-72 w-auto max-w-full cursor-pointer rounded-lg"
              data-testid="message-image"
            />
            {open && createPortal(
              <div
                className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/90 p-4"
                onClick={() => setOpen(false)}
                data-testid="image-lightbox"
                style={{ inset: 0 }}
              >
                <img
                  src={src}
                  alt={msg.media_filename || "image"}
                  className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  onClick={() => setOpen(false)}
                  className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/25"
                  aria-label="Close"
                  data-testid="lightbox-close"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>,
              document.body
            )}
          </>
        )}

        {msg.message_type === "file" && (
          <a href={src} target="_blank" rel="noreferrer" className={`mb-1 flex items-center gap-3 rounded-lg px-3 py-2 ${mediaBg}`} data-testid="message-file">
            <span className={`flex h-10 w-10 items-center justify-center rounded-full ${fileIconBg}`}>
              <FileText className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block truncate font-medium">{msg.media_filename}</span>
              <span className={`text-xs ${dark ? "text-white/50" : "text-black/50"}`}>{formatBytes(msg.media_size)}</span>
            </span>
            <Download className={`ml-1 h-4 w-4 ${dark ? "text-white/40" : "text-black/40"}`} aria-hidden="true" />
          </a>
        )}

        {msg.message_type === "voice" && (
          <div className="mb-1">
            <VoiceNote
              src={src}
              duration={msg.duration}
              mine={mine}
              dark={dark}
              cached={!mine ? msg.voice_translation : null}
              onTranslate={!mine && onVoiceTranslate ? () => onVoiceTranslate(msg) : null}
              hideOriginal={!dark && !mine && translationActive}
            />
          </div>
        )}

        {msg.message_type === "call" && (
          <div className="flex items-center gap-2 py-0.5">
            <Phone className={`h-4 w-4 ${dark ? "text-[#2ac67b]" : "text-[#0e8f7e]"}`} aria-hidden="true" />
            <span>{msg.content}</span>
          </div>
        )}

        {msg.content && msg.message_type !== "call" && (
          <div>
            <div className="whitespace-pre-wrap break-words">
              {linkify(textShown, `underline decoration-1 underline-offset-2 break-all ${dark ? "text-[#8ecbff]" : "text-[#0b6ff0]"}`)}
            </div>
            {hasTranslation && (
              <button
                onClick={() => setShowOrig((o) => !o)}
                className={`mt-0.5 flex items-center gap-1 text-[10px] ${dark ? "text-white/40 hover:text-white/70" : "text-black/40 hover:text-black/70"}`}
                data-testid="translation-toggle"
              >
                <Languages className="h-3 w-3" aria-hidden="true" />
                {showOrig ? "Show translation" : "Show original"}
              </button>
            )}
          </div>
        )}

        <div className={`mt-0.5 flex items-center justify-end gap-1 text-[10.5px] ${metaColor}`}>
          <span>{time}</span>
          {mine && (msg.read ? <CheckCheck className={`h-3.5 w-3.5 ${dark ? "text-[#2ac67b]" : "text-[#34b7f1]"}`} aria-hidden="true" /> : <Check className="h-3.5 w-3.5" aria-hidden="true" />)}
        </div>
      </div>
    </div>
  );
}
